/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Turns one checkout — every ticked activity, every seat — into a single batch
 * payload for POST /arcee/application/table/save/records/.
 *
 * The sibling of buildEventBatch.js, and deliberately the same shape: a pure
 * function, an ordered operations list, and `@ref` to point a child row at a
 * parent that does not exist yet.
 *
 * WHY EACH SEAT IS TWO LINKED OPERATIONS. `event_pass.registration_id` is
 * required, and the registration's id does not exist until it is inserted. So
 * the registration is named with `ref` and the pass refers to it with `@ref`,
 * and the engine substitutes the real id as it writes. Both land or neither
 * does — split across two calls, a failed pass would leave somebody registered
 * with no way through the gate and nothing to undo it.
 *
 * ONE PAIR PER SEAT, not one operation holding every seat. `ref` names exactly
 * one row, because `@ref` can only stand for a single id; the batch endpoint
 * refuses a `ref` on a list. Ten seats is therefore twenty operations, which is
 * why SEATS_PER_CHECKOUT sits where it does — see the cap below.
 *
 * WHAT THIS FUNCTION DOES NOT SEND, and must never start sending:
 *
 *   • `user_id` / `created_by_id` — sent as "@me", resolved from the session.
 *     A browser-supplied user id is a pass minted in somebody else's name.
 *   • `registered_at` — sent as "@now". It decides who was first in a queue,
 *     so it cannot come from a device clock.
 *   • `qr_token` / `passcode` — omitted entirely. The platform generates both
 *     before the insert, and asks for them back through `returning`.
 */

import envConfig from '../../../config/envConfig';
import { CONFIRMED, PENDING, HOLD } from './registrationRules';

/** The table names this page writes. */
export const REGISTRATION_TABLE = 'event_registration';
export const PASS_TABLE = 'event_pass';

/**
 * Statuses a caller may ask a seat to be written with.
 *
 * CONFIRMED is the default and is not listed: anything unrecognised becomes
 * CONFIRMED, so listing it would make no difference. PENDING is a waiting-list
 * place and HOLD is a place being paid for right now.
 */
const WRITABLE_STATUSES = new Set([PENDING, HOLD]);

/**
 * Columns read back out of each pass row.
 *
 * They exist only once the row is written, and nothing else can carry them to
 * the visitor: a public read of event_pass would show everyone else's.
 */
export const PASS_RETURNING = [
  'passcode',
  // A PICTURE of the token, not the token. Whoever holds an entry token can
  // present it at the gate, and the holder needs to show it rather than know
  // it — so the server draws it and the secret never reaches the page.
  { column: 'qr_token', as: 'qr_svg' },
];

/**
 * The most operations one batch may carry, mirroring the server's own cap.
 *
 * MUST MATCH batch_save.MAX_OPERATIONS. It is duplicated rather than fetched
 * because the modal has to know a seat's cost before it offers the stepper,
 * and a round trip to find that out would make the ceiling arrive after the
 * visitor had already set a number it refuses.
 *
 * Two per seat with a pass, a waiting-list seat costs one — see
 * maxSeatsPerCheckout, which does that division so no caller repeats it.
 */
export const MAX_OPERATIONS = 150;

/**
 * The most seats one checkout may hold, from the operation cap above.
 *
 * DERIVED, NEVER TYPED. A seat with a pass is two operations — the
 * registration, and the pass naming it with `@ref` — so the seat ceiling is
 * half the operation cap. A waiting-list seat mints no pass and costs one.
 *
 * Expressed this way so there is exactly one number to change: raise
 * MAX_OPERATIONS alongside the server and every ceiling that depends on it
 * follows, instead of a second constant drifting out of step and offering a
 * visitor seats the batch builder below then refuses.
 */
export function maxSeatsPerCheckout(mintsPass) {
  return mintsPass ? Math.floor(MAX_OPERATIONS / 2) : MAX_OPERATIONS;
}

/**
 * Extra answers, keyed by the column each one belongs in.
 *
 * NOTHING CALLS THIS TODAY. The event page asks no questions any more — the
 * entry form came from stored button configuration, and that connection is
 * gone. It is kept because the shape of the problem will not change if the page
 * ever asks its own: a map of `column -> "@form.<fieldId>"` and the answers
 * under those field ids, producing the object that rides in `extras`.
 */
export function answersForExtras(answers, answerMap) {
  const given = answers && typeof answers === 'object' ? answers : {};
  const map = answerMap && typeof answerMap === 'object' ? answerMap : {};
  const out = {};

  Object.entries(map).forEach(([column, source]) => {
    if (typeof source !== 'string' || !source.startsWith('@form.')) return;
    const fieldId = source.slice('@form.'.length);
    const value = given[fieldId];
    // An unanswered optional question writes nothing rather than an empty
    // string, so "not asked" and "left blank" stay distinguishable.
    if (value !== undefined && value !== null && value !== '') {
      out[column] = value;
    }
  });

  return out;
}

/** What a pass keeps about the person, in this order. */
const USER_INFO_FIELDS = ['first_name', 'last_name', 'user_name', 'email', 'country_code', 'mobile_number'];

/**
 * Who this pass belongs to, as the pass keeps it: their name, username, email
 * and mobile number from the signed-in account — every field the session
 * already gives us, and only the ones it has. `undefined` when it knows none.
 *
 * Stored on the pass because the organizer who scans it later cannot read
 * other users' accounts — `user_id` alone names nobody. It is a copy taken at
 * registration, shown at the check-in desk and never trusted for a decision;
 * a later change to the account does not reach passes already issued.
 */
export function userInfoOf(who) {
  const info = {};
  USER_INFO_FIELDS.forEach((field) => {
    const value = String(who?.[field] ?? '').trim();
    if (value) info[field] = value;
  });
  return Object.keys(info).length ? info : undefined;
}

/** The gender codes a participant may be given — the profile's own codes. */
export const PARTICIPANT_GENDERS = ['FEMALE', 'MALE', 'PREFER_NOT_TO_SAY'];

const PARTICIPANT_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Who a seat is for, when the booker named somebody else for it in the entry
 * modal — shaped exactly like `userInfoOf`, so the check-in desk and the
 * participant export read it without knowing the difference.
 *
 * `undefined` unless a first name was given: a row without one is an empty
 * row, and an empty row's seat stays in the booker's name exactly as it did
 * before participants could be named. Values the modal already flagged as
 * malformed (an email with no @, an age of 400) are left out rather than
 * stored — every field here is optional, so a bad one costs nothing but itself.
 */
export function attendeeInfoOf(attendee, { countryCode = '+91' } = {}) {
  const read = field => String(attendee?.[field] ?? '').trim();
  const firstName = read('first_name');
  if (!firstName) return undefined;

  const info = { first_name: firstName };
  const lastName = read('last_name');
  if (lastName) info.last_name = lastName;

  const email = read('email');
  if (email && PARTICIPANT_EMAIL.test(email)) info.email = email;

  const mobile = read('mobile_number').replace(/\D/g, '');
  if (mobile) {
    info.country_code = countryCode;
    info.mobile_number = mobile;
  }

  const gender = read('gender').toUpperCase();
  if (PARTICIPANT_GENDERS.includes(gender)) info.gender = gender;

  const age = read('age');
  if (/^\d{1,2}$/.test(age) && Number(age) >= 1) info.age = Number(age);

  return info;
}

/** "Ravi Kumar" for a seat whose participant was named, else ''. */
export function attendeeNameOf(seat) {
  const info = attendeeInfoOf(seat && seat.attendee);
  return info ? [info.first_name, info.last_name].filter(Boolean).join(' ') : '';
}

/**
 * Builds the batch for one press of Continue.
 *
 * `requests` is what the entry modal produces: one entry per seat, each naming
 * its activity, its seat number and the checkout id shared by the whole press.
 *
 * `answers` / `answerMap` are unused by the page today — see answersForExtras.
 */
export function buildRegistrationBatch(requests, {
  eventId, rules, organizationId, applicationId, answers, answerMap,
  applicationUserId, userInfo,
} = {}) {
  // Left from debugging. It printed every seat of every booking -- names,
  // emails and phone numbers among them -- into the browser console on each
  // press of Continue.
  const seats = Array.isArray(requests) ? requests : [];

  if (!seats.length) {
    throw new Error('Nothing was chosen to register for.');
  }
  if (eventId === undefined || eventId === null || eventId === '') {
    throw new Error('Cannot register without the event this is for.');
  }
  if (!rules || !rules.canRegister) {
    // Reached only by a bug: the button that opens the modal is drawn from the
    // same rules. Refusing here means a page that somehow offered entry it
    // should not still writes nothing.
    throw new Error('Entry is not open for this event.');
  }

  const operations = [];
  seats.forEach((seat, index) => {
    operations.push(...seatOperations(seat, {
      index, eventId, rules, answers, answerMap, applicationUserId, userInfo,
    }));
  });

  if (operations.length > MAX_OPERATIONS) {
    // SAID IN SEATS, because that is the only unit the visitor chose anything
    // in. The old wording counted database writes -- "152 writes, limit 150"
    // -- which is true, is the reason, and tells somebody who picked 76 seats
    // nothing they can act on.
    const allowed = maxSeatsPerCheckout(Boolean(rules && rules.mintsPass));
    const over = seats.length - allowed;
    throw new Error(
      `You can book up to ${allowed} seats in one go, and ${seats.length} were `
      + `chosen. Please remove ${over} ${over === 1 ? 'seat' : 'seats'} — you `
      + 'can book the rest straight after this booking.'
    );
  }

  return {
    organization_id: organizationId ?? envConfig.organizationId,
    application_id: applicationId ?? envConfig.applicationId,
    operations,
  };
}

/**
 * The rows ONE seat becomes — the single definition of what a seat is.
 *
 * Extracted so the paid path (buildCheckoutPayload.js) builds the identical
 * rows rather than its own copy. Everything the header warns must never come
 * from the browser — `user_id`, `registered_at`, `qr_token` — is decided here,
 * once. Two copies of that rule would eventually disagree, and the copy that
 * drifted would be the one minting passes.
 *
 * `refPrefix` namespaces the ref. In a free batch one press is one batch, so
 * `r0` is unambiguous; in a paid checkout every seat's operations are
 * concatenated into ONE batch server-side, so the refs must be unique across
 * the whole payment, not just within a line.
 */
export function seatOperations(seat, {
  index, eventId, rules, answers, answerMap, applicationUserId, userInfo, refPrefix = 'r',
} = {}) {
  // A WHITELIST, NOT A ONE-OFF TEST FOR PENDING. This read
  // `seatStatus === PENDING ? PENDING : CONFIRMED`, which silently turned
  // every other value into CONFIRMED -- so asking for a HOLD got a confirmed
  // entry instead, writing the seat as paid for before anybody had paid. Any
  // status this page does not know is still CONFIRMED, which is what every
  // existing caller relies on.
  const status = WRITABLE_STATUSES.has(rules.seatStatus) ? rules.seatStatus : CONFIRMED;
  const mintsPass = Boolean(rules.mintsPass);
  const answered = answersForExtras(answers, answerMap);
  const operations = [];

  {
    const activityId = seat && seat.activity && seat.activity.id;
    if (activityId === undefined || activityId === null || activityId === '') {
      throw new Error('One of the chosen activities has no id.');
    }

    const registration = {
      event_id: eventId,
      event_activity_id: activityId,
      status,
      registered_at: '@now',
      created_by_id: '@me',
      // The pair the database's unique rules are built from. One press of
      // Continue mints one checkout id and each seat carries its own number, so
      // a REPEAT of a seat is refused, a genuine second seat is accepted, and a
      // SECOND VISIT collides with the seat 1 the first visit already wrote.
      //
      // They ride in `extras` rather than in columns of their own, so the model
      // gains nothing for them. Both indexes are built from these two keys as
      // JSON expressions — see configure_athlohive_event_page. Strings, because
      // that is how they are compared inside a JSON column: a numeric seat would
      // be stored as `2` and read back as `"2"` by the index, and the two would
      // not match.
      extras: {
        // The answers first, so the two keys below can never be overwritten by
        // a question that happens to share their name.
        ...answered,
        checkout_id: String(seat.checkoutId),
        seat_no: String(seat.seatNo),
      },
    };

    // A pass row needs the registration's id, so that registration is named.
    // Without a pass there is nothing to point at it, and naming it would only
    // add a ref nobody reads.
    if (!mintsPass) {
      operations.push({ table: REGISTRATION_TABLE, data: registration });
      return operations;
    }

    // A seat the booker named somebody else for carries THAT person on its
    // pass; every other seat carries the booker, exactly as before.
    const passUserInfo = attendeeInfoOf(seat && seat.attendee) || userInfo;

    const ref = seatRef(index, refPrefix);
    operations.push({ ref, table: REGISTRATION_TABLE, data: registration });
    operations.push({
      table: PASS_TABLE,
      data: {
        registration_id: `@${ref}`,
        event_id: eventId,
        // The signed-in id when the page knows it. Falls back to '@me' when it
        // does not: the server then refuses the batch with 403
        // no_application_user, which is the existing no-session path and sends
        // the visitor to sign in. Without the fallback an absent id would be
        // dropped by JSON.stringify and the pass would belong to nobody.
        user_id: applicationUserId ?? '@me',
        status: 'ISSUED',
        // Shown at the check-in desk; see userInfoOf and attendeeInfoOf.
        // ...(userInfo ? { user_info: userInfo } : {}),
        ...(passUserInfo ? { user_info: passUserInfo } : {}),
        // qr_token and passcode are absent ON PURPOSE — see the header.
      },
      returning: PASS_RETURNING,
    });
  }

  return operations;
}

/** The ref naming the row that IS the thing sold, for a given seat. */
export function seatRef(index, refPrefix = 'r') {
  return `${refPrefix}${index}`;
}

/**
 * What to call one seat's activity.
 *
 * The entry modal builds its seats as {activity, seatNo, seatsForActivity,
 * checkoutId} — the name lives on the activity. A caller that has already
 * flattened it is accepted too, so this reads correctly from either shape
 * rather than printing "undefined" when it guesses wrong.
 */
export function seatName(seat) {
  const fromActivity = seat && seat.activity && seat.activity.name;
  return fromActivity || (seat && seat.name) || 'This activity';
}

/**
 * Pairs each seat with the pass the server returned for it.
 *
 * The reply lists operations in the order they were sent, so the passes come
 * back in the order the seats went out. Matching on that order rather than on
 * ids keeps this independent of whether the primary key is an integer or a
 * uuid.
 *
 * A seat whose pass was withheld — a waiting-list entry — simply has none, and
 * that is a success with nothing to show at a gate rather than a failure.
 */
export function passesFromResult(result, requests) {
  const operations = (result && result.operations) || [];
  const seats = Array.isArray(requests) ? requests : [];

  const returned = operations
    .filter(operation => operation && operation.table === PASS_TABLE)
    .map(operation => (operation.returned || [])[0])
    .filter(Boolean);

  return returned.map((row, index) => {
    const seat = seats[index] || {};
    // The modal's own seats carry the activity object, not a flattened name —
    // reading only `seat.name` printed "undefined · seat 1" on every pass.
    const name = seatName(seat);
    const attendee = attendeeNameOf(seat);
    return {
      // activityName: seat.seatsForActivity > 1
      //   ? `${name} · seat ${seat.seatNo}`
      //   : name,
      activityName: attendee
        ? `${attendee} · ${name}`
        : (seat.seatsForActivity > 1 ? `${name} · seat ${seat.seatNo}` : name),
      // Shaped for EventPassViewer, which takes label/value pairs so the field
      // names stay the page's business rather than a fixed contract.
      fields: [{ label: 'Pass code', value: row.passcode }],
      qrSvg: row.qr_svg,
    };
  });
}
